import React, { createContext, useEffect, useReducer } from 'react';
import { getAuth } from 'firebase/auth';
import firebase from 'firebase/compat/app';
import 'firebase/compat/app-check';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/functions';
import 'firebase/compat/storage';
import Loader from 'components/Loader';
import { useDispatch } from 'store';
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';
import { setError } from 'store/reducers/config';
import { AuthProps, FirebaseContextType } from 'types/auth';
import authDefinitions from 'utils/authDefinitions';

let authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
const subdomain = getSubdomain(window.location.href);
const fullHostname = new URL(window.location.href).hostname;

switch (true) {
  case import.meta.env.MODE === 'development':
    authDomain = 'onthemove-35981.firebaseapp.com';
    break;
  case fullHostname === 'onthemove-35981--beta-vite-xj4mvz09.web.app':
    authDomain = 'onthemove-35981--beta-vite-xj4mvz09.web.app';
    break;
  case subdomain === 'staff':
    authDomain = 'staff.onthemove.com';
    break;
  case subdomain === 'clients':
    authDomain = 'clients.onthemove.com';
    break;
  case subdomain === 'talent':
    authDomain = 'talent.onthemove.com';
    break;
  default:
    break;
}

if (!firebase.apps.length && import.meta.env.MODE !== 'test') {
  firebase.initializeApp({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: authDomain,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
  });
}

if (import.meta.env.MODE === 'test') {
  firebase.initializeApp({
    apiKey: 'test',
    projectId: 'test',
    appId: 'test'
  });
}



const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const db = firebase.firestore();
const storage = firebase.storage();
const admin = firebase.firestore;
const auth = getAuth();
const firestore = firebase.firestore;
const functions = firebase.functions();

if (import.meta.env.MODE === 'development') {
  functions.useEmulator('localhost', 5001);
  storage.useEmulator('localhost', 9199);
}

db.settings({
  ignoreUndefinedProperties: true,
  merge: true
});


const FirebaseContext = createContext<FirebaseContextType | null>(null);

export const FirebaseProvider = ({ children }: { children: React.ReactElement }) => {
  const { currentUser } = firebase.auth();
  const [state, dispatch] = useReducer(authReducer, initialState);
  const reduxDispatch = useDispatch();

  const logOutWithMessage = (message: string) => {
    console.log('message', message);
    reduxDispatch(setError(message));
    dispatch({
      type: LOGOUT
    });
  };

  useEffect(
    () =>
      firebase.auth().onAuthStateChanged((user: any) => {
        if (user) {
          const contactsRef = db.collection('contacts');
          const getContact = async () => {
            const emailHits = await contactsRef
              .where('email1', '==', user.email)
              // .where('email1', '==', 'test@example.com') // hardcode for testing
              .get()
              .then((querySnapshot) => {
                return querySnapshot.docs.map((doc) => {
                  return { ...doc.data(), id: doc.id };
                });
              })
              .catch((error): [] => {
                console.error('error getting email:', error);
                return [];
              });
            const hasOneEmail = emailHits.length === 1;
            const hasMultipleEmails = emailHits.length > 1;
            const hasNoEmail = emailHits.length === 0;
            const theContact = emailHits[0];
            if (hasOneEmail) {
              dispatch({
                type: LOGIN,
                payload: {
                  isLoggedIn: true,
                  user: authDefinitions(user, theContact)
                }
              });
              reduxDispatch(setError(''));
            }
            if (hasMultipleEmails) {
            }
            if (hasNoEmail) {
              logOutWithMessage(
                'The email you tried to log in with is not in our records.  Please try another email, or contact an admin to help you get set up.'
              );
            }
          };
          getContact().catch((error) => {
            console.log('error in catch', error);
            logOutWithMessage(
              `${error.message} The email we have on file may be different than the one you are using to login.`
            );
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      }),
    [dispatch] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const firebaseEmailPasswordSignIn = (email: string, password: string) =>
    firebase.auth().signInWithEmailAndPassword(email, password);

  const firebaseGoogleSignIn = async () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/drive');
    provider.addScope('https://www.googleapis.com/auth/calendar');
    provider.setCustomParameters({
      access_type: 'offline',
      prompt: 'consent'
    });
    try {
      if (authDomain !== 'onthemove-35981.firebaseapp.com') {
        firebase.auth().signInWithRedirect(provider);
      } else {
        firebase.auth().signInWithPopup(provider);
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  };


  const firebaseTwitterSignIn = () => {
    const provider = new firebase.auth.TwitterAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const firebaseFacebookSignIn = () => {
    const provider = new firebase.auth.FacebookAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const firebaseRegister = async (email: string, password: string) => {
    console.log('firebaseRegister');
    return firebase.auth().createUserWithEmailAndPassword(email, password);
  };

  const logout = () => firebase.auth().signOut();

  const resetPassword = async (email: string) => {
    await firebase.auth().sendPasswordResetEmail(email);
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <FirebaseContext.Provider
      value={{
        ...state,
        db,
        admin,
        auth,
        storage,
        currentUser,
        functions,
        firebaseRegister,
        firestore,
        firebaseEmailPasswordSignIn,
        login: () => {},
        firebaseGoogleSignIn,
        firebaseTwitterSignIn,
        firebaseFacebookSignIn,
        logout,
        resetPassword,
        updateProfile
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

export default FirebaseContext;

function getSubdomain(url: string) {
  var host = new URL(url).hostname;
  var parts = host.split('.');
  if (parts.length > 2) {
    return parts[0];
  }
  return null;
}
